import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Save, Loader2, BarChart2, ChevronDown, RefreshCcw } from 'lucide-react';
import ImageModal from './components/ImageModal';
import { useAuth } from './context/AuthContext';

/**
 * AnnotationPage — "Annotation tool"
 *
 * Per-auction-dataset annotation screen. Lots are loaded from the database
 * (via `/annotation_dataset_lots`). The DB is populated on demand via
 * `/annotation_sync_dataset`, which snapshots the dataset from blob storage
 * into the `DatasetLots` table.
 *
 * Styling mirrors the Test Rig page (same outer container, title, and
 * dataset badge) for a consistent look.
 */
const AnnotationPage = () => {
  const { user: currentUser } = useAuth();
  const [datasets, setDatasets] = useState([]);
  const [isLoadingDatasets, setIsLoadingDatasets] = useState(true);
  const [isRefreshingDatasets, setIsRefreshingDatasets] = useState(false);
  const [datasetLoadError, setDatasetLoadError] = useState('');
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [datasetSearch, setDatasetSearch] = useState('');
  const [isDatasetDropdownOpen, setIsDatasetDropdownOpen] = useState(false);
  const datasetDropdownRef = useRef(null);
  const lotsFetchSequenceRef = useRef(0);

  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState({});
  const [syncing, setSyncing] = useState(false);

  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedLotNum, setSelectedLotNum] = useState(null);

  const refreshDatasets = async () => {
    if (isRefreshingDatasets) return;
    setIsRefreshingDatasets(true);
    setDatasetLoadError('');
    try {
      const response = await axios.post('/refresh_test_datasets');
      const nextDatasets = Array.isArray(response?.data?.datasets) ? response.data.datasets : [];
      setDatasets(nextDatasets);
      if (nextDatasets.length === 0) {
        setSelectedDataset(null);
        setDatasetSearch('');
        return;
      }
      const currentPrefix = selectedDataset?.prefix || '';
      const matchingSelection = nextDatasets.find((entry) => (entry.prefix || '') === currentPrefix) || null;
      if (matchingSelection) {
        setSelectedDataset(matchingSelection);
        setDatasetSearch(matchingSelection.name || matchingSelection.prefix || '');
      } else {
        setSelectedDataset(null);
        setDatasetSearch('');
      }
    } catch (error) {
      console.error('Error refreshing annotation datasets:', error);
      setDatasetLoadError(error?.response?.data?.message || error?.message || 'Failed to refresh datasets.');
    } finally {
      setIsRefreshingDatasets(false);
    }
  };

  const fetchDatasets = async () => {
    setIsLoadingDatasets(true);
    setDatasetLoadError('');
    try {
      const response = await axios.get('/test_datasets');
      const nextDatasets = Array.isArray(response?.data?.datasets) ? response.data.datasets : [];
      setDatasets(nextDatasets);

      if (nextDatasets.length === 0) {
        setSelectedDataset(null);
        setDatasetSearch('');
        return;
      }

      const currentPrefix = selectedDataset?.prefix || '';
      const matchingSelection = nextDatasets.find((entry) => (entry.prefix || '') === currentPrefix) || null;

      if (matchingSelection) {
        setSelectedDataset(matchingSelection);
        setDatasetSearch(matchingSelection.name || matchingSelection.prefix || '');
        return;
      }

      setSelectedDataset(null);
      setDatasetSearch('');
    } catch (error) {
      console.error('Error loading annotation datasets:', error);
      setDatasets([]);
      setSelectedDataset(null);
      setDatasetSearch('');
      setDatasetLoadError(error?.response?.data?.message || error?.message || 'Failed to load datasets.');
    } finally {
      setIsLoadingDatasets(false);
    }
  };

  const fetchLots = async (prefix) => {
    const requestSequence = ++lotsFetchSequenceRef.current;
    setLoading(true);
    setError('');
    try {
      const response = await axios.get('/annotation_dataset_lots', {
        params: { dataset_prefix: prefix || '' },
      });
      if (requestSequence !== lotsFetchSequenceRef.current) return;
      const next = Array.isArray(response?.data?.lots) ? response.data.lots : [];
      setLots(next);
      const nextDrafts = {};
      next.forEach((lot) => {
        const key = String(lot.lot_number || '');
        if (!key) return;
        const ref = lot.refactored_actual;
        nextDrafts[key] = ref === null || ref === undefined ? '' : String(ref);
      });
      setDrafts(nextDrafts);
    } catch (e) {
      if (requestSequence !== lotsFetchSequenceRef.current) return;
      setError(e?.response?.data?.message || e.message || 'Failed to load lots for this auction.');
      setLots([]);
    } finally {
      if (requestSequence !== lotsFetchSequenceRef.current) return;
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDatasets();
  }, []);

  useEffect(() => {
    if (!selectedDataset) {
      lotsFetchSequenceRef.current += 1;
      setLots([]);
      setDrafts({});
      setLoading(false);
      return;
    }
    fetchLots(selectedDataset?.prefix || '');
  }, [selectedDataset]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datasetDropdownRef.current && !datasetDropdownRef.current.contains(event.target)) {
        setIsDatasetDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const normalizedDatasetSearch = datasetSearch.trim().toLowerCase();
  const filteredDatasets = datasets.filter((dataset) => {
    if (!normalizedDatasetSearch) return true;
    const name = (dataset?.name || '').toLowerCase();
    const prefix = (dataset?.prefix || '').toLowerCase();
    return name.includes(normalizedDatasetSearch) || prefix.includes(normalizedDatasetSearch);
  });

  const selectDataset = (dataset) => {
    if (!dataset) return;
    setSelectedDataset(dataset);
    setDatasetSearch(dataset.name || dataset.prefix || '');
    setIsDatasetDropdownOpen(false);
  };

  const datasetBadgeText = selectedDataset
    ? `${selectedDataset.name || selectedDataset.prefix || 'Container root'}${
        selectedDataset.data_points !== undefined ? ` (${selectedDataset.data_points} data points)` : ''
      }`
    : (isLoadingDatasets
      ? 'Loading datasets...'
      : (datasetLoadError
        ? 'Dataset load failed'
        : (datasets.length > 0 ? 'Select a dataset' : 'No datasets available')));

  const onDraftChange = (lotNumber, value) => {
    setDrafts((prev) => ({ ...prev, [lotNumber]: value }));
  };

  const saveLot = async (lot) => {
    const lotNumber = String(lot.lot_number || '');
    if (!lotNumber) return;
    const raw = drafts[lotNumber];
    const trimmed = typeof raw === 'string' ? raw.trim() : raw;
    let payloadValue;
    if (trimmed === '' || trimmed === null || trimmed === undefined) {
      payloadValue = null;
    } else {
      const parsed = Number(trimmed);
      if (!Number.isFinite(parsed) || parsed < 0) {
        setError(`Lot ${lotNumber}: actual override must be a non-negative number.`);
        return;
      }
      payloadValue = parsed;
    }

    setSaving((prev) => ({ ...prev, [lotNumber]: true }));
    setError('');
    try {
      const response = await axios.post('/annotation_update_actual', {
        dataset_prefix: selectedDataset?.prefix || '',
        lot_number: lotNumber,
        refactored_actual: payloadValue,
      });
      const saved = response?.data?.refactored_actual;
      const savedByUserId = response?.data?.annotated_by_user_id;
      const savedByUsername = response?.data?.annotated_by_username;
      setLots((prev) => prev.map((item) =>
        String(item.lot_number || '') === lotNumber
          ? {
              ...item,
              refactored_actual: saved === undefined ? payloadValue : saved,
              annotated_by_user_id: savedByUserId !== undefined ? savedByUserId : (currentUser?.id ?? null),
              annotated_by_username: savedByUsername !== undefined ? savedByUsername : (currentUser?.username ?? null),
            }
          : item
      ));
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to save actual override.');
    } finally {
      setSaving((prev) => ({ ...prev, [lotNumber]: false }));
    }
  };

  const syncDataset = async () => {
    if (syncing) return;
    setSyncing(true);
    setError('');
    try {
      await axios.post('/annotation_sync_dataset', { dataset_prefix: selectedDataset?.prefix || '' });
      await fetchLots(selectedDataset?.prefix || '');
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to sync dataset from storage.');
    } finally {
      setSyncing(false);
    }
  };

  const formatMoney = (value) => {
    if (value === null || value === undefined) return '-';
    const n = Number(value);
    if (!Number.isFinite(n)) return '-';
    return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="w-full max-w-none p-3 sm:p-6 bg-white rounded-lg shadow-md">
      <div className="flex flex-col gap-3 mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">Annotation tool</h1>
          <div className="flex items-center px-3 sm:px-4 py-2 bg-blue-50 text-blue-700 rounded border border-blue-100">
            <BarChart2 size={18} className="mr-2 flex-shrink-0" />
            <span className="text-xs sm:text-sm font-medium">
              Dataset: {datasetBadgeText}
            </span>
          </div>
        </div>
        <div className="flex flex-col md:flex-row md:items-stretch md:flex-nowrap gap-2">
          <div ref={datasetDropdownRef} className="relative md:flex-[2.4]">
            <div className="flex items-center bg-slate-50 p-2 rounded-lg border border-slate-200 h-full">
              <span className="text-sm font-medium text-slate-700 mr-2">Dataset:</span>
              <input
                value={datasetSearch}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  const normalized = nextValue.trim().toLowerCase();
                  const exactMatch = datasets.find((dataset) => {
                    const datasetName = (dataset?.name || '').trim().toLowerCase();
                    const datasetPrefix = (dataset?.prefix || '').trim().toLowerCase();
                    return normalized && (datasetName === normalized || datasetPrefix === normalized);
                  }) || null;
                  setDatasetSearch(nextValue);
                  setSelectedDataset(exactMatch);
                  setIsDatasetDropdownOpen(true);
                }}
                onFocus={() => setIsDatasetDropdownOpen(true)}
                disabled={isLoadingDatasets}
                placeholder={isLoadingDatasets ? 'Loading datasets...' : 'Search datasets...'}
                className={`w-full bg-white border border-slate-300 rounded px-2 py-1 outline-none focus:border-blue-500 text-xs font-medium ${
                  isLoadingDatasets ? 'opacity-60 cursor-not-allowed' : ''
                }`}
              />
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => setIsDatasetDropdownOpen((previous) => !previous)}
                className="ml-2 inline-flex items-center justify-center rounded border border-slate-300 bg-white px-1.5 py-1 text-slate-600 hover:bg-slate-100"
                title={isDatasetDropdownOpen ? 'Close dataset list' : 'Open dataset list'}
                aria-label={isDatasetDropdownOpen ? 'Close dataset list' : 'Open dataset list'}
              >
                <ChevronDown size={14} className={`transition-transform ${isDatasetDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => refreshDatasets()}
                disabled={isRefreshingDatasets}
                className={`ml-1 inline-flex items-center justify-center rounded border px-1.5 py-1 transition-colors ${
                  isRefreshingDatasets
                    ? 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'
                    : 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100'
                }`}
                title="Repull dataset list from blob storage"
                aria-label="Repull dataset list from blob storage"
              >
                <RefreshCcw size={14} className={isRefreshingDatasets ? 'animate-spin' : ''} />
              </button>
            </div>
            {isDatasetDropdownOpen && (
              <div className="absolute z-20 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-xl">
                {isLoadingDatasets ? (
                  <div className="px-3 py-2 text-xs text-slate-500">Loading datasets...</div>
                ) : filteredDatasets.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-slate-500">
                    {datasetLoadError
                      ? `Failed to load datasets: ${datasetLoadError}`
                      : (datasets.length > 0 ? 'No matching datasets.' : 'No datasets available.')}
                  </div>
                ) : (
                  filteredDatasets.map((dataset) => (
                    <button
                      key={dataset.id || dataset.prefix || '__root__'}
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        selectDataset(dataset);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs border-b border-slate-100 last:border-b-0 hover:bg-slate-50 ${
                        (selectedDataset?.prefix || '') === (dataset.prefix || '') ? 'bg-blue-50 text-blue-700' : 'text-slate-700'
                      }`}
                    >
                      <div className="font-semibold">{dataset.name}</div>
                      <div className="text-[11px] text-slate-500">
                        {(dataset.prefix || '__root__')} • {dataset.data_points || 0} data points • {dataset.image_count || 0} images
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          <div className="flex items-center text-xs text-slate-500 md:ml-auto px-2">
            {selectedDataset ? <span>{lots.length} lots</span> : null}
          </div>
        </div>
      </div>

      {datasetLoadError && (
        <div className="mb-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
          {datasetLoadError}
        </div>
      )}
      {error && (
        <div className="mb-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="p-4 font-semibold text-slate-700 w-24">Lot #</th>
              <th className="p-4 font-semibold text-slate-700 w-28">Image</th>
              <th className="p-4 font-semibold text-slate-700 w-32 text-right">Actual</th>
              <th className="p-4 font-semibold text-slate-700 w-64">Actual Override</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={4} className="px-3 py-6 text-center text-slate-500">
                  <Loader2 size={16} className="inline animate-spin mr-2" />
                  Loading lots…
                </td>
              </tr>
            )}
            {!loading && lots.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-6 text-center text-slate-500">
                  {selectedDataset
                    ? 'No lots found for this auction.'
                    : 'Select an auction to see its lots.'}
                </td>
              </tr>
            )}
            {!loading && lots.map((lot) => {
              const lotNumber = String(lot.lot_number || '');
              const images = Array.isArray(lot.image_urls) ? lot.image_urls : [];
              const isSaving = !!saving[lotNumber];
              const draftValue = drafts[lotNumber] ?? '';
              const hasOverride =
                lot.refactored_actual !== null && lot.refactored_actual !== undefined;
              return (
                <tr key={lotNumber} className="hover:bg-slate-50 border-b border-slate-100 last:border-b-0">
                  <td className="px-1.5 py-1 border-r align-top font-mono text-slate-800">{lotNumber || '—'}</td>
                  <td className="px-1.5 py-1 border-r align-top">
                    <div className="flex flex-wrap gap-1">
                      {images.map((url, uidx) => (
                        <img
                          key={uidx}
                          src={url}
                          alt={`Lot ${lotNumber} ${uidx + 1}`}
                          loading="lazy"
                          decoding="async"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedImage(url);
                            setSelectedImages(images);
                            setSelectedLotNum(lotNumber);
                          }}
                          className="w-10 h-10 object-cover rounded shadow-sm hover:scale-[1.5] hover:z-10 transition-transform cursor-pointer ring-2 ring-emerald-400"
                        />
                      ))}
                      {images.length === 0 && (
                        <div className="w-10 h-10 bg-slate-200 rounded flex items-center justify-center text-[10px] text-slate-400">No Img</div>
                      )}
                    </div>
                  </td>
                  <td className="p-4 align-top text-right font-semibold text-slate-800">
                    {formatMoney(lot.actual_total)}
                  </td>
                  <td className="p-4 align-top">
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={draftValue}
                        onChange={(e) => onDraftChange(lotNumber, e.target.value)}
                        placeholder={hasOverride ? '' : 'e.g. 1200.00'}
                        disabled={isSaving}
                        className="w-32 rounded border border-slate-300 bg-white px-2 py-1 text-sm text-slate-800"
                      />
                      <button
                        type="button"
                        onClick={() => saveLot(lot)}
                        disabled={isSaving}
                        className="inline-flex items-center gap-1 rounded border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                        title="Save actual override"
                      >
                        {isSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                        Save
                      </button>
                    </div>
                    {hasOverride && (
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] text-slate-500">
                        <span>Saved: {formatMoney(lot.refactored_actual)}</span>
                        {lot.annotated_by_username
                          && lot.annotated_by_username !== currentUser?.username && (
                          <span
                            className="font-mono text-slate-700 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded"
                            title={`Annotated by ${lot.annotated_by_username}`}
                          >
                            By: {lot.annotated_by_username}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <ImageModal
        isOpen={!!selectedImage}
        onClose={() => {
          setSelectedImage(null);
          setSelectedImages([]);
          setSelectedLotNum(null);
        }}
        imageUrl={selectedImage}
        imageUrls={selectedImages}
        lotNum={selectedLotNum}
      />
    </div>
  );
};

export default AnnotationPage;
